
import { supabase } from "@/integrations/supabase/client";
import { v4 as uuidv4 } from "uuid";
import { FamilyProfile } from "./types";
import { 
  saveFamilyProfileToLocalStorage, 
  getFamilyProfileFromLocalStorage,
  hasFamilyProfileInLocalStorage
} from "./localStorage";
import {
  saveFamilyProfileToSupabase,
  getFamilyProfileFromSupabase,
} from "./supabaseOperations";
import { cmToInches, inchesToCm, gToLb, lbToG } from "./unitConversion";

// Save profile to localStorage and Supabase if user is logged in
export const saveFamilyProfile = async (profile: FamilyProfile): Promise<void> => {
  // Always save to localStorage for immediate access
  saveFamilyProfileToLocalStorage(profile);
  
  // If user is logged in, save to Supabase
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  
  try {
    await saveFamilyProfileToSupabase(profile, user.id);
  } catch (error) {
    console.error('Error saving family profile to Supabase:', error);
  }
};

// Fetch family profile from Supabase if user is logged in, otherwise from localStorage
export const getFamilyProfile = async (): Promise<FamilyProfile | null> => {
  try {
    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user) {
      // Try to get profile from Supabase first
      const supabaseProfile = await getFamilyProfileFromSupabase(user.id);
      
      if (supabaseProfile) {
        // Update localStorage with the latest data
        saveFamilyProfileToLocalStorage(supabaseProfile);
        return supabaseProfile;
      }
    }
    
    // Fallback to localStorage if no user or no Supabase data
    return getFamilyProfileFromLocalStorage();
  } catch (error) {
    console.error('Error fetching family profile:', error);
    // Fallback to localStorage on error
    return getFamilyProfileFromLocalStorage();
  }
};

// Synchronous version for components that can't use async/await
export const getFamilyProfileSync = (): FamilyProfile | null => {
  return getFamilyProfileFromLocalStorage();
};

// Check if a profile exists (in localStorage)
export const hasFamilyProfile = (): boolean => {
  return hasFamilyProfileInLocalStorage();
};

// Update part of a profile
export const updateFamilyProfile = async (updates: Partial<FamilyProfile>): Promise<void> => {
  const currentProfile = await getFamilyProfile() || {};
  await saveFamilyProfile({ ...currentProfile, ...updates });
};

// Get unit preference for height (true for metric, false for imperial)
export const getHeightUnitPreference = (): boolean => {
  const profile = getFamilyProfileSync();
  // Default to metric (cm) if not specified
  return profile?.preferMetricHeight !== false;
};

// Get unit preference for weight (true for metric, false for imperial)
export const getWeightUnitPreference = (): boolean => {
  const profile = getFamilyProfileSync();
  // Default to metric (g) if not specified
  return profile?.preferMetricWeight !== false;
};

// Toggle height unit preference
export const toggleHeightUnit = async (): Promise<boolean> => {
  const currentPreference = getHeightUnitPreference();
  await updateFamilyProfile({ preferMetricHeight: !currentPreference });
  return !currentPreference;
};

// Toggle weight unit preference
export const toggleWeightUnit = async (): Promise<boolean> => {
  const currentPreference = getWeightUnitPreference();
  await updateFamilyProfile({ preferMetricWeight: !currentPreference });
  return !currentPreference;
};

// Re-export unit conversion helpers
export { cmToInches, inchesToCm, gToLb, lbToG };

// Sync local family profile with Supabase
export const syncFamilyProfile = async (): Promise<void> => {
  try {
    // Check if user is logged in
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    
    // Check if profile exists in localStorage
    const localProfile = getFamilyProfileFromLocalStorage();
    if (!localProfile) return;
    
    // Save to Supabase
    await saveFamilyProfile(localProfile);
  } catch (error) {
    console.error('Error syncing family profile:', error);
  }
};
